import React, { useMemo } from 'react';

import { Link } from 'react-router-dom';

import { Popover, Space } from 'antd';

import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import type { IconName } from '@/shared/ui/Icon';

import styles from './IconTextBadgeList.module.css';

export type IconTextBadgeItem = {
  id: string;
  icon?: IconName;
  text: string;
  linkTo?: string;
  badge?: {
    text: string;
    color?: string;
  };
};

type IconTextBadgeListProps = {
  items: IconTextBadgeItem[];
  maxVisibleCount?: number;
  showIcon?: boolean;
  showIconInPopover?: boolean;
  iconSize?: number;
  itemMaxWidth?: number | string;
};

export const IconTextBadgeList: React.FC<IconTextBadgeListProps> = ({
  items,
  maxVisibleCount,
  showIcon = true,
  showIconInPopover = true,
  iconSize = 16,
  itemMaxWidth = 200,
}) => {
  const sortedItems = useMemo(() => [...items], [items]);

  const shouldShowPopover = maxVisibleCount !== undefined && sortedItems.length > maxVisibleCount;
  const visibleItems = shouldShowPopover ? sortedItems.slice(0, maxVisibleCount) : sortedItems;
  const remainingItems = shouldShowPopover ? sortedItems.slice(maxVisibleCount) : [];

  return (
    <Space size={[4, 6]} wrap align="center" className={styles.iconTextBadgeList}>
      {visibleItems.map((item) => (
        <div key={item.id} className={styles.itemWrapper} style={{ maxWidth: itemMaxWidth }}>
          {showIcon && item.icon && <Icon name={item.icon} size={iconSize} className={styles.icon} />}
          <EllipsisText text={item.text} className={styles.text}>
            {item.linkTo ? <Link to={item.linkTo}>{item.text}</Link> : item.text}
          </EllipsisText>
          {item.badge && <span className={styles.badge}>{item.badge.text}</span>}
        </div>
      ))}
      {shouldShowPopover && remainingItems.length > 0 && (
        <Popover
          content={
            <div className={styles.popoverContent}>
              {remainingItems.map((item) => (
                <div key={item.id} className={styles.itemWrapper} style={{ maxWidth: itemMaxWidth }}>
                  {showIconInPopover && item.icon && <Icon name={item.icon} size={iconSize} className={styles.icon} />}
                  <EllipsisText text={item.text} className={styles.text}>
                    {item.linkTo ? <Link to={item.linkTo}>{item.text}</Link> : item.text}
                  </EllipsisText>
                  {item.badge && <span className={styles.badge}>{item.badge.text}</span>}
                </div>
              ))}
            </div>
          }
          trigger="hover"
          placement="topLeft">
          <span className={styles.remainingCount}>+{remainingItems.length}</span>
        </Popover>
      )}
    </Space>
  );
};
